/**
 * taskFacts — the small derivations a task's chrome keeps re-deriving.
 *
 * Status colour, priority colour, the label for a status and how urgent a due
 * date is were each written out three times — once in `TaskDetail`'s header
 * badges, once in the Details tab's fact grid, and again the moment the
 * context pane wanted the same answers. Three copies is how the header comes
 * to say "amber" while the tile says "yellow".
 *
 * Pure functions, no React, no i18n instance: whoever calls passes `t`.
 */

import { parseServerDate } from '../../utils/functions';

export type TaskTone = 'default' | 'success' | 'warning' | 'danger';

type Translate = (key: string) => string;

/** Badge colour for a task status. */
export const taskStatusColor = (status?: string | null): string => {
  switch ((status || '').toLowerCase()) {
    case 'completed': return 'green';
    case 'in_progress': return 'blue';
    case 'active': return 'blue';
    case 'pending': return 'yellow';
    case 'todo': return 'gray';
    case 'deleted':
    case 'archived': return 'red';
    default: return 'gray';
  }
};

/** Human label for a task status. */
export const taskStatusLabel = (status: string | undefined | null, t: Translate): string => {
  switch ((status || '').toLowerCase()) {
    case 'completed': return t('Completed');
    case 'in_progress': return t('In Progress');
    case 'todo': return t('To Do');
    case 'archived': return t('Archived');
    case 'deleted': return t('Deleted');
    default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : t('To Do');
  }
};

/** Badge colour for a task priority. */
export const taskPriorityColor = (priority?: string | null): string => {
  switch ((priority || '').toLowerCase()) {
    case 'low': return 'green';
    case 'medium': return 'yellow';
    case 'high': return 'amber';
    case 'critical': return 'red';
    default: return 'yellow';
  }
};

export interface TaskDueInfo {
  text: string;
  urgency: 'overdue' | 'soon' | 'upcoming';
}

/**
 * How close a due date is — or null when there is nothing to say (no date, or
 * the task is already done, when "Overdue" would be a lie).
 */
export const taskDueInfo = (
  dueDate: string | null | undefined,
  status: string | undefined,
  t: Translate
): TaskDueInfo | null => {
  if (!dueDate || (status || '').toLowerCase() === 'completed') return null;
  const due = parseServerDate(dueDate);
  if (!due) return null;
  const diffMs = due.getTime() - Date.now();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffMs < 0) return { text: t('Overdue'), urgency: 'overdue' };
  if (diffHours <= 48) return { text: t('Due Soon'), urgency: 'soon' };
  if (diffHours / 24 <= 7) return { text: t('This Week'), urgency: 'upcoming' };
  return null;
};

/** The tone a due date lends the tile it sits in. */
export const taskDueTone = (info: TaskDueInfo | null): TaskTone =>
  info?.urgency === 'overdue' ? 'danger'
  : info?.urgency === 'soon' ? 'warning'
  : 'default';
